import { useState, useEffect } from 'react';
import './App.css';

const NAV_LINKS = [
  { label: 'About', href: '#about' },
  { label: 'Services', href: '#services' },
  { label: 'Why Us', href: '#why' },
  { label: 'Contact', href: '#contact' },
];

const SERVICES = [
  {
    icon: '🌐',
    image: '/images/air-cargo.jpeg',
    title: 'Global Trading',
    description:
      'Facilitating seamless import and export operations across multiple industries and international markets with precision and reliability.',
  },
  {
    icon: '🔗',
    image: '/images/warehouse.jpeg',
    title: 'Procurement & Sourcing',
    description:
      'Identifying reliable suppliers and delivering cost-effective sourcing solutions tailored precisely to your business requirements.',
  },
  {
    icon: '🚚',
    image: '/images/logistics-truck.jpeg',
    title: 'Distribution & Supply Chain',
    description:
      'Enhancing product movement and logistics efficiency through strategic distribution networks and optimised supply chain support.',
  },
  {
    icon: '📈',
    image: '/images/business-handshake.jpeg',
    title: 'Business Development',
    description:
      'Supporting organisations in expanding their market presence and establishing valuable commercial partnerships for sustained growth.',
  },
  {
    icon: '🔍',
    image: '/images/digital-logistics.jpeg',
    title: 'Market Research & Analysis',
    description:
      'Providing actionable insights and market intelligence to help businesses make informed, confident strategic decisions.',
  },
  {
    icon: '🤝',
    image: '/images/world-network.jpeg',
    title: 'Strategic Partnerships',
    description:
      'Building and nurturing long-term alliances with global partners to create mutually beneficial commercial opportunities.',
  },
];

const WHY_ITEMS = [
  {
    icon: '⚖️',
    title: 'Professional & Ethical Practices',
    text: 'We uphold the highest standards of integrity and transparency in every transaction and partnership we enter.',
  },
  {
    icon: '🌍',
    title: 'Strong Global Network',
    text: 'Our extensive network of international partners enables us to connect businesses across markets worldwide.',
  },
  {
    icon: '🎯',
    title: 'Customer-Focused Approach',
    text: 'Every solution we deliver is tailored to the unique needs and goals of our clients.',
  },
  {
    icon: '🔒',
    title: 'Reliable & Transparent Operations',
    text: 'Consistency, accountability, and open communication are the pillars of how we do business.',
  },
  {
    icon: '🏆',
    title: 'Commitment to Excellence',
    text: 'We set the bar high — in quality, service delivery, and the outcomes we achieve for our clients.',
  },
  {
    icon: '📊',
    title: 'Long-Term Value Creation',
    text: 'Our strategies are built to deliver sustainable growth and enduring value for all stakeholders.',
  },
];

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = (e, href) => {
    e.preventDefault();
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav className={`navbar${scrolled ? ' scrolled' : ''}`}>
      <div className="container">
        <div className="navbar__inner">
          <a href="#hero" className="navbar__brand" onClick={(e) => handleNav(e, '#hero')}>
            <img src="/images/logo/logo.png" alt="EverMarket Commerce" className="navbar__logo" />
            <div className="navbar__brand-text">
              <span className="navbar__brand-name">EVERMARKET COMMERCE LTD</span>
              <span className="navbar__brand-tagline">Empowering Global Trade</span>
            </div>
          </a>

          <ul className="navbar__links">
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                <a href={l.href} onClick={(e) => handleNav(e, l.href)}>
                  {l.label}
                </a>
              </li>
            ))}
            <li>
              <a
                href="#contact"
                className="navbar__cta"
                onClick={(e) => handleNav(e, '#contact')}
              >
                Get in Touch
              </a>
            </li>
          </ul>

          <button
            className="navbar__hamburger"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      <div className={`navbar__mobile${open ? ' open' : ''}`}>
        {NAV_LINKS.map((l) => (
          <a key={l.href} href={l.href} onClick={(e) => handleNav(e, l.href)}>
            {l.label}
          </a>
        ))}
        <a href="#contact" onClick={(e) => handleNav(e, '#contact')}>
          Get in Touch
        </a>
      </div>
    </nav>
  );
}

function Hero() {
  const scrollTo = (href) =>
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section id="hero" className="hero">
      <div className="container">
        <div className="hero__grid">
          <div className="hero__content">
            <p className="hero__eyebrow">EVERMARKET COMMERCE LTD</p>
            <h1 className="hero__title">
              Empowering <span>Global Trade.</span> Creating Lasting Value.
            </h1>
            <p className="hero__description">
              We connect businesses, markets, and opportunities across industries through strategic
              sourcing, trading, distribution, and commercial solutions — helping organisations
              achieve sustainable growth and operational excellence.
            </p>
            <div className="hero__actions">
              <button className="btn-primary" onClick={() => scrollTo('#services')}>
                Explore Services
              </button>
              <button className="btn-outline" onClick={() => scrollTo('#contact')}>
                Contact Us
              </button>
            </div>
          </div>

          <div className="hero__visual">
            <div className="hero__photo-frame">
              <img src="/images/hero-airplane.jpeg" alt="Global trade network" className="hero__photo" />
              <div className="hero__photo-badge">
                <div className="hero__stats">
                  <div>
                    <p className="hero__stat-value">50+</p>
                    <p className="hero__stat-label">Markets Served</p>
                  </div>
                  <div>
                    <p className="hero__stat-value">6</p>
                    <p className="hero__stat-label">Core Services</p>
                  </div>
                  <div>
                    <p className="hero__stat-value">100%</p>
                    <p className="hero__stat-label">Client Focus</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="about" className="section">
      <div className="container">
        <div className="about__grid">
          <div className="about__content">
            <div className="section__header" style={{ textAlign: 'left', marginBottom: 32 }}>
              <p className="section__eyebrow" style={{ justifyContent: 'flex-start' }}>
                About Us
              </p>
              <h2 className="section__title">
                A Diversified Commerce & Trading Company
              </h2>
            </div>

            <p>
              EVERMARKET COMMERCE LTD is dedicated to facilitating efficient business transactions
              across domestic and international markets. With a strong commitment to quality,
              integrity, and customer satisfaction, we provide end-to-end commercial solutions that
              support businesses in sourcing, procurement, distribution, and market expansion.
            </p>
            <p>
              Our experienced team works closely with clients and partners to identify opportunities,
              optimise supply chains, and deliver measurable, sustainable results — building
              relationships founded on trust and transparency.
            </p>

            <div className="about__values">
              {[
                'Quality-Driven Solutions',
                'Integrity in Every Deal',
                'Global Market Access',
                'Long-Term Partnerships',
                'Supply Chain Excellence',
                'Client-First Mindset',
              ].map((v) => (
                <div key={v} className="about__value-item">
                  {v}
                </div>
              ))}
            </div>
          </div>

          <div className="about__visual">
            <div className="about__image-frame">
              <div className="about__frame-icon">🏛️</div>
              <h3 className="about__frame-title">Built on Trust & Transparency</h3>
              <p className="about__frame-text">
                We are more than a trading company — we are a strategic commerce partner dedicated
                to creating lasting value for every client, partner, and stakeholder we serve.
              </p>
              <div className="about__metrics">
                <div className="about__metric">
                  <span className="about__metric-value">Global</span>
                  <p className="about__metric-label">Reach & Network</p>
                </div>
                <div className="about__metric">
                  <span className="about__metric-value">5+</span>
                  <p className="about__metric-label">Service Verticals</p>
                </div>
                <div className="about__metric">
                  <span className="about__metric-value">100%</span>
                  <p className="about__metric-label">Ethical Practices</p>
                </div>
                <div className="about__metric">
                  <span className="about__metric-value">24/7</span>
                  <p className="about__metric-label">Partner Support</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section id="services" className="section section--gray">
      <div className="container">
        <div className="section__header">
          <p className="section__eyebrow">Our Services</p>
          <h2 className="section__title">Comprehensive Commerce Solutions</h2>
          <p className="section__subtitle">
            From global trade facilitation to market intelligence, we deliver the full spectrum of
            commercial services your business needs to grow and thrive.
          </p>
        </div>

        <div className="services__grid">
          {SERVICES.map((s) => (
            <div key={s.title} className="service-card">
              <div className="service-card__image-wrap">
                <img src={s.image} alt={s.title} className="service-card__image" />
                <div className="service-card__icon-overlay">{s.icon}</div>
              </div>
              <h3 className="service-card__title">{s.title}</h3>
              <p className="service-card__description">{s.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Why() {
  return (
    <section id="why" className="section">
      <div className="container">
        <div className="why__grid">
          <div>
            <div className="section__header" style={{ textAlign: 'left', marginBottom: 40 }}>
              <p className="section__eyebrow" style={{ justifyContent: 'flex-start' }}>
                Why Choose Us
              </p>
              <h2 className="section__title">The EVERMARKET Advantage</h2>
              <p
                className="section__subtitle"
                style={{ textAlign: 'left', margin: 0, maxWidth: '100%' }}
              >
                We combine global reach, professional expertise, and an unwavering commitment to
                client success in everything we do.
              </p>
            </div>

            <div className="why__list">
              {WHY_ITEMS.map((item) => (
                <div key={item.title} className="why__item">
                  <div className="why__item-icon">{item.icon}</div>
                  <div>
                    <p className="why__item-title">{item.title}</p>
                    <p className="why__item-text">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="why__mission-vision">
            <div className="mv-card">
              <p className="mv-card__badge">Our Vision</p>
              <h3 className="mv-card__title">
                To Become a Trusted Global Commerce Partner
              </h3>
              <p className="mv-card__text">
                We aspire to be the global commerce partner of choice — recognised for innovation,
                reliability, and excellence in delivering business solutions that stand the test of
                time.
              </p>
            </div>

            <div className="mv-card">
              <p className="mv-card__badge">Our Mission</p>
              <h3 className="mv-card__title">
                Creating Sustainable Value for All Stakeholders
              </h3>
              <p className="mv-card__text">
                To create sustainable value for clients, partners, and stakeholders by delivering
                efficient commerce solutions, fostering strategic relationships, and driving business
                growth across global markets.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [form, setForm] = useState({ name: '', company: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <section id="contact" className="section section--navy">
      <div className="container">
        <div className="section__header">
          <p className="section__eyebrow">Contact Us</p>
          <h2 className="section__title">Let's Build Something Together</h2>
          <p className="section__subtitle">
            We welcome opportunities to collaborate with businesses, investors, suppliers, and
            strategic partners worldwide.
          </p>
        </div>

        <div className="contact__grid">
          <div>
            <h3 className="contact__info-title">Get in Touch</h3>
            <p className="contact__info-text">
              Whether you're looking for a reliable trading partner, exploring sourcing opportunities,
              or seeking to expand into new markets — EVERMARKET COMMERCE LTD is ready to help.
              Reach out and let's start a conversation.
            </p>

            <div className="contact__detail">
              <div className="contact__detail-icon">✉️</div>
              <div>
                <p className="contact__detail-label">Email</p>
                <p className="contact__detail-value">info@evermarketcommerce.com</p>
              </div>
            </div>

            <div className="contact__detail">
              <div className="contact__detail-icon">🌐</div>
              <div>
                <p className="contact__detail-label">Operations</p>
                <p className="contact__detail-value">International — Multiple Markets</p>
              </div>
            </div>

            <div className="contact__detail">
              <div className="contact__detail-icon">💼</div>
              <div>
                <p className="contact__detail-label">Enquiries</p>
                <p className="contact__detail-value">
                  Trading, Procurement, Partnerships, Distribution
                </p>
              </div>
            </div>
          </div>

          <div className="contact__form">
            <h3 className="contact__form-title">Send Us a Message</h3>

            {submitted ? (
              <div className="form-success">
                Thank you for reaching out. A member of our team will be in touch with you shortly.
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="John Smith"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="company">Company</label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      placeholder="Your Company Ltd"
                      value={form.company}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="john@company.com"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <select
                    id="subject"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select an enquiry type</option>
                    <option value="trading">Global Trading</option>
                    <option value="procurement">Procurement & Sourcing</option>
                    <option value="distribution">Distribution & Supply Chain</option>
                    <option value="business-dev">Business Development</option>
                    <option value="partnership">Strategic Partnership</option>
                    <option value="other">General Enquiry</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Tell us about your business needs and how we can help..."
                    value={form.message}
                    onChange={handleChange}
                    required
                  />
                </div>

                <button type="submit" className="form-submit">
                  Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  const scrollTo = (href) =>
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer__inner">
          <div className="footer__brand">
            <img src="/images/logo/logo.png" alt="EverMarket Commerce" className="footer__logo" />
            <p className="footer__brand-name">EVERMARKET COMMERCE LTD</p>
            <p className="footer__brand-tagline">Empowering Global Trade</p>
            <p className="footer__brand-text">
              A diversified commerce and trading company dedicated to facilitating efficient business
              transactions across domestic and international markets.
            </p>
          </div>

          <div>
            <p className="footer__heading">Quick Links</p>
            <ul className="footer__links">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a href={l.href} onClick={(e) => { e.preventDefault(); scrollTo(l.href); }}>
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="footer__heading">Services</p>
            <ul className="footer__links">
              {['Global Trading', 'Procurement & Sourcing', 'Supply Chain', 'Business Development', 'Market Research'].map((s) => (
                <li key={s}>
                  <a href="#services" onClick={(e) => { e.preventDefault(); scrollTo('#services'); }}>
                    {s}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="footer__bottom">
          <p className="footer__copy">
            &copy; {new Date().getFullYear()} <span>EVERMARKET COMMERCE LTD</span>. All rights
            reserved.
          </p>
          <p className="footer__copy">
            info@evermarketcommerce.com
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <>
      <Navbar />
      <main>
        <Hero />
        <About />
        <Services />
        <Why />
        <Contact />
      </main>
      <Footer />
    </>
  );
}
